import { captureMessage } from "../config/sentry";

const SLOW_REQUEST_THRESHOLD_MS = 2000;

interface ApiTiming {
  url: string;
  method: string;
  duration: number;
  status: number;
  timestamp: number;
}

/** Rolling window of recent API timings (last 100) */
const timings: ApiTiming[] = [];
const MAX_TIMINGS = 100;

/**
 * Record an API call's timing. Call this from the HTTP client interceptor.
 */
export function recordApiTiming(timing: ApiTiming): void {
  timings.push(timing);
  if (timings.length > MAX_TIMINGS) {
    timings.shift();
  }

  // Report slow requests to Sentry
  if (timing.duration > SLOW_REQUEST_THRESHOLD_MS) {
    captureMessage(
      `Slow API request: ${timing.method} ${timing.url} (${Math.round(timing.duration)}ms)`,
      "warning",
    );
  }
}

/**
 * Calculate percentile stats from recorded timings.
 */
export function getApiStats(): {
  count: number;
  p50: number;
  p95: number;
  p99: number;
  slowRequests: number;
} {
  if (timings.length === 0) {
    return { count: 0, p50: 0, p95: 0, p99: 0, slowRequests: 0 };
  }

  const sorted = [...timings].sort((a, b) => a.duration - b.duration);
  const p = (pct: number) =>
    sorted[Math.floor(sorted.length * pct)]?.duration ?? 0;

  return {
    count: sorted.length,
    p50: Math.round(p(0.5)),
    p95: Math.round(p(0.95)),
    p99: Math.round(p(0.99)),
    slowRequests: timings.filter((t) => t.duration > SLOW_REQUEST_THRESHOLD_MS)
      .length,
  };
}

/**
 * Get the raw timings array (for debug/display purposes).
 */
export function getApiTimings(): readonly ApiTiming[] {
  return timings;
}
